import isEqual from 'lodash/isEqual'
import {
    useCallback, useMemo, useState,
} from 'react'
import ContractBasisInformationForm from 'app/Apps/Pricing/LaneFees/ContractBasisInformationForm'
import LaneGroupInformationForm from 'app/Apps/Pricing/LaneFees/LaneGroupInformationForm'

import getFields from 'app/Apps/Pricing/LaneGroups/LaneFees/LaneFeesTable/LaneFeesForm.fields'
import LaneGroupContextWrapper from 'app/Apps/Pricing/LaneGroups/LaneGroupContextWrapper'

import {
    ContractBasis, LaneGroup,
} from 'app/Apps/Pricing/Pricing.types'

import useSimpleApiRequest from 'app/hooks/useSimpleApiRequest'
import LaneGroupRequests from 'app/requests/ContractBasis/LaneGroup/LaneGroup.requests'
import HistoryBlockPrompt from 'app/shared-components/HistoryBlockPrompt'
import {
    LaneFeesForm, LaneFeesFormContext,
} from 'app/shared-components/LaneFees'
import SkyNetStepper from 'app/shared-components/SkyNetStepper'
import SkyNetStepperButtons from 'app/shared-components/SkyNetStepper/SkyNetStepperButtons'
import LaneFeesSetStatusButtons from 'app/shared-components/LaneFees/LaneFeesSetStatusButtons'
import useGetDefaultLaneFeesData from 'app/shared-components/LaneFees/hooks/useGetDefaultLaneFeesData'

export type LaneFeesFormValue = {
    contractBasis?: ContractBasis | { id: number },
    laneGroup?: LaneGroup | { id: number }
}

const fields = {
    getFields,
}

export default function LaneFeesCreateStepper({
    initialValue,
    onSuccess,
}: Readonly<{
    initialValue?: LaneFeesFormValue,
    onSuccess: (item: { id: number }) => void
}>) {
    const [
        value,
        setValue,
    ] = useState<LaneFeesFormValue>(initialValue)

    const [
        edited,
        setEdited,
    ] = useState<boolean>(false)

    const [
        activeStep,
        setActiveStep,
    ] = useState<number>(0)

    const nextAction = useCallback(() => {
        setActiveStep(activeStep + 1)
    }, [activeStep])

    const {
        mutate: create,
        isPending,
    } = useSimpleApiRequest({
        onSuccess,
        name: 'LaneFeesCreate',
        requestParams: LaneGroupRequests.createLaneFee({
            laneGroupId: value?.laneGroup?.id,
        }),
        title: 'Lane Fees Created',
    })

    const defaultFormValue = useGetDefaultLaneFeesData({
        laneGroupId: value?.laneGroup?.id,
    })

    const getActionButtonsPanel = useCallback(({
        value: laneFeesValue, isValid,
    }) => {
        return (
            <SkyNetStepperButtons
                isLoading={isPending}
                isValid={isValid}
                customButtons={[<LaneFeesSetStatusButtons
                    key="set-status-buttons"
                    value={laneFeesValue}
                    onCreate={(newValue) => {
                        setEdited(false)
                        create(newValue)
                    }}
                    disabled={!isValid}
                    isLoading={isPending}
                />]}
                disabled={!isValid}
                canGoBack={activeStep > 1}
                backAction={() => {
                    setValue({
                        laneGroup: value?.laneGroup,
                        contractBasis: value?.contractBasis,
                    })
                    setActiveStep(activeStep - 1)
                }}
            />
        )
    }, [
        activeStep,
        create,
        isPending,
        value?.contractBasis,
        value?.laneGroup,
    ])

    const stepperSteps = useMemo(() => {
        return [
            {
                key: '1',
                label: 'Select Contract Basis',
                expanded: true,
                content: (
                    <>
                        <ContractBasisInformationForm
                            id={value?.contractBasis?.id}
                            update={(updated: { contractBasis: { id: number } }) => {
                                setEdited(true)
                                setValue(updated)
                            }}
                            disabled={activeStep !== 0}
                        />
                        <SkyNetStepperButtons
                            isValid={Boolean(value?.contractBasis?.id)}
                            canEdit={activeStep > 0}
                            nextAction={nextAction}
                            editAction={() => {
                                setValue({
                                    contractBasis: value?.contractBasis,
                                })
                                setActiveStep(0)
                            }}
                        />
                    </>
                ),
            },
            {
                key: '2',
                label: 'Select Lane Group',
                expanded: activeStep > 0,
                content: (
                    <>
                        <LaneGroupInformationForm
                            id={value?.laneGroup?.id}
                            update={(updated: {
                                laneGroup: {
                                    id: number,
                                }
                            }) => {
                                setEdited(true)
                                setValue({
                                    ...value,
                                    ...updated,
                                })
                            }}
                            disabled={activeStep !== 1}
                            contractId={value?.contractBasis?.id}
                        />
                        <SkyNetStepperButtons
                            isValid={Boolean(value?.laneGroup?.id)}
                            canEdit={activeStep > 1}
                            nextAction={nextAction}
                            editAction={() => {
                                setActiveStep(1)
                            }}
                        />
                    </>
                ),
            },
            {
                key: '3',
                label: 'Specify Fees',
                expanded: activeStep > 1,
                content: (
                    <LaneGroupContextWrapper id={value?.laneGroup?.id}>
                        <LaneFeesFormContext.Provider value={fields}>
                            <LaneFeesForm
                                data={defaultFormValue}
                                getActionButtonsPanel={getActionButtonsPanel}
                                onChange={(val) => {
                                    setEdited(!isEqual(val, defaultFormValue))
                                }}
                            />
                        </LaneFeesFormContext.Provider>
                    </LaneGroupContextWrapper>
                ),
            },
        ]
    }, [
        activeStep,
        defaultFormValue,
        getActionButtonsPanel,
        nextAction,
        value,
    ])

    return (
        <>
            <SkyNetStepper
                activeStep={activeStep}
                steps={stepperSteps}
            />
            <HistoryBlockPrompt
                when={edited}
                positiveLabel="Discard"
                isPositiveAlert
                promptText="You are about to leave this page, your progress will be lost. Would you like to discard your changes anyway?"
            />
        </>
    )
}
